// The interestng brand logo — the SAME image the staff frontend uses, applied the
// SAME way. The frontend renders:
//   <img src=".../interestng_logo_updated.jpeg" class="logo-image"
//        style="height:40px; width:auto">
//   .logo-image { object-fit:contain; mix-blend-mode:multiply;
//                 filter:contrast(1.1) brightness(1.05); }
// `mix-blend-mode: multiply` drops the white JPEG background against a light
// surface, so the mark sits cleanly without a visible box.

import React from 'react';

// CRA serves /public at the app's base — PUBLIC_URL, from `homepage` in
// package.json. Derive it rather than hardcoding: the base moved from "/b2c/"
// to "/" on 2026-08-29 and this kept working untouched.
// Same file, same path shape as the staff app (frontend/public/layout/images/…),
// so "which logo is live" is answered identically in both codebases. It used to
// live at /brand/interestng_logo.jpeg here — byte-identical, just renamed, which
// made the two apps look like they were on different assets.
const SRC = `${process.env.PUBLIC_URL}/layout/images/interestng_logo_updated.jpeg`;

/**
 * @param height    rendered height (px). Ignored when `fluid`.
 * @param onDark    true → the logo sits on a DARK surface. `mix-blend:multiply`
 *   only drops the white background on a LIGHT surface; on dark we sit the mark on
 *   a small white chip instead so it stays legible.
 * @param iconOnly  true → show only the %-mark portion (for the collapsed rail),
 *   by cropping the wide JPEG to its left square via object-fit.
 * @param fluid     true → hand size + crop to the CALLER'S STYLESHEET (target
 *   `.ing-logo`) and keep only the colour treatment here. The sidebar needs this:
 *   its logo is full-width when the rail is open, a cropped square when it's
 *   collapsed, and full-width again inside the mobile drawer — three states, one
 *   of them breakpoint-dependent, which an inline style simply cannot express.
 *   Passing a `height`/`iconOnly` guess instead is what made the drawer show the
 *   cropped % mark whenever the DESKTOP rail happened to be collapsed.
 */
export default function BrandLogo({ height = 40, onDark = false, iconOnly = false, fluid = false, style, className, alt = 'interestng' }) {
  const img = (
    <img
      src={SRC}
      alt={alt}
      className={`ing-logo${className ? ` ${className}` : ''}`}
      style={{
        // Full logo is ~4.6:1; the % mark occupies the left ~1/9. Crop to a square
        // for icon-only so a narrow rail shows just the mark, not a squashed strip.
        ...(fluid ? null : {
          height,
          width: iconOnly ? height : 'auto',
          objectFit: iconOnly ? 'cover' : 'contain',
          objectPosition: iconOnly ? 'left center' : 'center',
        }),
        verticalAlign: 'middle',
        // Multiply drops the white JPEG background — only works on LIGHT surfaces.
        mixBlendMode: onDark ? 'normal' : 'multiply',
        filter: 'contrast(1.1) brightness(1.05)',
        ...style,
      }}
    />
  );

  // On a dark surface, wrap in a white chip so the (opaque-white-bg) logo reads.
  if (onDark) {
    return (
      <span
        className={className}
        style={{ display: 'inline-flex', background: '#fff', borderRadius: 8, padding: '4px 8px', lineHeight: 0 }}
      >
        {React.cloneElement(img, { className: 'ing-logo', style: { ...img.props.style, mixBlendMode: 'multiply' } })}
      </span>
    );
  }
  return img;
}
